// pages/tabs/InitialSettingsTab.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Card, Form, Button, Spinner, Badge, Row, Col, Alert } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import {
  fetchHouseSettingsThunk,
  updateHouseSettingsThunk,
  clearHouseSettingsError,
} from "../../../../store/slices/houseSettingsSlice";

import "../../../../css/master-common.css";

// convert API date -> input[type=date] value (YYYY-MM-DD)
const toDateInput = (v) => {
  const s = String(v ?? "").trim();
  if (!s) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : "";
};

export default function InitialSettingsTab({  home_id,
  activeTab,
  onRefreshProfile, }) {
  const dispatch = useDispatch();
  const { data, loading, saving, error } = useSelector((s) => s.houseSettings);

  // ✅ tab key must match HouseProfilePage key
  const isActive = activeTab === "settings";

  // ✅ permissions + superadmin bypass
  const perms = useSelector((s) => s.permissions?.map?.house_master) || {};
  const userRole = useMemo(() => {
    try {
      return String(localStorage.getItem("user_role") || "").toLowerCase();
    } catch {
      return "";
    }
  }, []);
  const canUpdate = userRole === "superadmin" ? true : perms.can_update !== false;

  const [mode, setMode] = useState("view"); // "view" | "edit"
  const [form, setForm] = useState({
    home_id: "",
    water_balance: "",
    slrm_bal: "",
    balance: "",
    last_reading: "",
    last_bill_date: "",
    slrm_last_bill_date: "",
  });

  const [initialForm, setInitialForm] = useState(null);

  // ✅ fetch ONLY when tab becomes active (because all tabs are mounted)
  useEffect(() => {
    if (!isActive) return;
    if (home_id) dispatch(fetchHouseSettingsThunk(home_id));
  }, [isActive, home_id, dispatch]);

  // prefill from API
  useEffect(() => {
    const next = {
      home_id: home_id ? String(home_id) : "",
      water_balance: data?.water_balance ?? "",
      slrm_bal: data?.slrm_bal ?? "",
      balance: data?.balance ?? "",
      last_reading: data?.last_reading ?? "",
      last_bill_date: toDateInput(data?.last_bill_date),
      slrm_last_bill_date: toDateInput(data?.slrm_last_bill_date),
    };

    setForm(next);
    setInitialForm(next);
    setMode("view"); // ✅ when fresh data comes, reset to view
  }, [data, home_id]);

  useEffect(() => {
    if (!error) return;
    toast.error(String(error));
    dispatch(clearHouseSettingsError());
  }, [error, dispatch]);

  const fields = [
    ["water_balance", "Water Bill Balance", "number"],
    ["slrm_bal", "SLRM Balance", "number"],
    ["balance", "Total Balance", "number"],
    ["last_reading", "Last Reading", "number"],
    ["last_bill_date", "Last Water Bill Date", "date"],
    ["slrm_last_bill_date", "Last SLRM Bill Date", "date"],
  ];

  const onClickEdit = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!home_id) return toast.error("home_id missing");
    if (!canUpdate) return toast.error("No update permission");

    setMode("edit");

    setTimeout(() => {
      const el = document.getElementById("init_water_balance");
      el?.focus?.();
      el?.select?.();
    }, 0);
  };

  const onCancel = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (initialForm) setForm(initialForm);
    setMode("view");
  };

  const onRefresh = () => {
    if (!home_id) return;
    dispatch(fetchHouseSettingsThunk(home_id));
    toast.info("Refreshing...");
  };

 const submit = async (e) => {
  e.preventDefault();
  e.stopPropagation();

  if (!home_id) return;
  if (!canUpdate) return toast.error("No update permission");

  const payload = {
    ...form,
    home_id: String(home_id),
    last_bill_date: form.last_bill_date
      ? String(form.last_bill_date)
      : null,
    slrm_last_bill_date: form.slrm_last_bill_date
      ? String(form.slrm_last_bill_date)
      : null,
  };

  const r = await dispatch(updateHouseSettingsThunk(payload));

  if (updateHouseSettingsThunk.fulfilled.match(r)) {
    toast.success("Initial settings updated ✅");

    setMode("view");

    dispatch(fetchHouseSettingsThunk(home_id));
    onRefreshProfile?.();
  } else {
    const msg =
      r?.payload?.message ||
      r?.payload ||
      r?.error?.message ||
      "Failed to update initial settings";

    toast.error(String(msg));
  }
};

  const isReadOnly = mode === "view" || !canUpdate;

  // ✅ important: since tabs are mounted, stop rendering when not active
  // (this also avoids unnecessary work)
  if (!isActive) return null;

  return (
    <div className="hm-form-page initialsettings-tab">
      <Row className="g-3">
        <Col xs={12}>
          <Card className="hm-form-card">
            <Card.Header className="hm-form-header">
              <div className="initialsettings-head">
                <div className="initialsettings-head-left">
                  <h5 className="mb-1 hm-form-title">Initial Settings</h5>
                  <div className="hm-form-subtitle">
                    Set opening balances and last meter/bill details for this house.
                  </div>
                </div>

                <div className="d-flex align-items-center gap-2">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    type="button"
                    onClick={onRefresh}
                    disabled={!home_id || loading}
                  >
                    {loading ? "Refreshing..." : "Refresh"}
                  </Button>

                  {home_id ? (
                    <Badge bg="light" text="dark" className="hm-form-badge">
                      Home ID: <span className="hm-form-badge-val">{home_id}</span>
                    </Badge>
                  ) : null}
                </div>
              </div>
            </Card.Header>

            <Card.Body className="hm-form-body">
              {!canUpdate ? (
                <Alert variant="warning" className="mb-3">
                  You don’t have permission to update initial settings.
                </Alert>
              ) : null}

              {loading ? (
                <div className="hm-form-loading">
                  <Spinner animation="border" />
                  <div className="mt-2 text-muted">Loading settings...</div>
                </div>
              ) : (
                <Form onSubmit={submit}>
                  <Row className="g-3">
                    {fields.map(([key, label, type]) => (
                      <Col md={6} key={key}>
                        <Form.Group className="hm-field">
                          <Form.Label className="hm-label">{label}</Form.Label>

                          <Form.Control
                            id={key === "water_balance" ? "init_water_balance" : undefined}
                            className="hm-control"
                            type={type}
                            value={form?.[key] ?? ""}
                            readOnly={isReadOnly}
                            style={isReadOnly ? { backgroundColor: "#f8fafc" } : undefined}
                            onChange={(e) => {
                              if (isReadOnly) return;
                              setForm((p) => ({ ...p, [key]: e.target.value }));
                            }}
                          />
                        </Form.Group>
                      </Col>
                    ))}

                    <Col xs={12}>
                      <div className="hm-actions">
                        <div className="hm-actions-left">
                          <span className="hm-help">
                            {mode === "view"
                              ? "Click Edit to modify values."
                              : "Click Update to save changes."}
                          </span>
                        </div>

                        <div className="hm-actions-right d-flex gap-2">
                          {mode === "view" ? (
                            <Button
                              variant="secondary"
                              className="hm-primary-btn"
                              type="button"
                              onClick={onClickEdit}
                              disabled={!home_id || !canUpdate}
                            >
                              Edit
                            </Button>
                          ) : (
                            <>
                              <Button
                                variant="light"
                                type="button"
                                className="hm-primary-btn"
                                onClick={onCancel}
                                disabled={saving}
                              >
                                Cancel
                              </Button>

                              <Button
                                type="submit"
                                className="hm-primary-btn"
                                disabled={saving || !home_id || !canUpdate}
                              >
                                {saving ? (
                                  <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Updating…
                                  </>
                                ) : (
                                  "Update"
                                )}
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Form>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
}